import { useRef, useState } from "react";

import type { CargaTrabalhoProfissional } from "@/modules/carga-trabalho/carga-trabalho.types";
import { carregarAlocacoesDoPeriodo, VERIFICACAO_ERROR_MESSAGE } from "./nova-obra.verificacoes";
import {
  combinarJustificativasEquipe,
  JUSTIFICATIVA_EQUIPE_MAX,
  limiteJustificativaEquipe,
} from "./nova-obra.equipe";
import type { EquipeObraTarget } from "@/modules/recursos/equipe-obra.types";
import type { Profissional } from "@/modules/recursos/profissionais.types";
import {
  detectarConflitos,
  detectarIndisponibilidades,
  papeisObrigatoriosPendentes,
  unirIndisponibilidades,
} from "@/modules/recursos";
import type {
  ConflitoNovaAlocacao,
  IndisponibilidadeNovaAlocacao,
  NovaAlocacao,
  PapelIncompletoItem,
} from "@/modules/recursos";

/** Diálogo de verificação em tela, na ordem em que aparecem. */
export type EtapaVerificacaoEquipe =
  | { tipo: "conflitos"; conflitos: ConflitoNovaAlocacao[] }
  | { tipo: "indisponivel"; itens: IndisponibilidadeNovaAlocacao[] }
  | { tipo: "incompleta"; itens: PapelIncompletoItem[] };

export interface VerificacoesEquipeParams {
  obra: EquipeObraTarget;
  /** Validação em tela das linhas; `false` interrompe antes de qualquer requisição. */
  validar: () => boolean;
  novas: NovaAlocacao[];
  /** Situação dos papéis obrigatórios (todos, adicionados ou não). */
  resumoObrigatorios: PapelIncompletoItem[];
  /** Profissionais da etapa (indisponibilidades da HU 1761). */
  listagem: Profissional[];
  onErroVerificacao: (mensagem: string) => void;
  /** Depois de todos os diálogos: recebe a justificativa única (ou `null`, sem divergência). */
  acaoFinal: (justificativa: string | null) => void;
}

/**
 * Verificações da Etapa 4 com as regras e os diálogos de Definir Equipe de Recursos (HU 2059,
 * nota da PO; HU 2530 RN08): (1) conflito com outra obra, (2) profissional indisponível e
 * (3) equipe incompleta, nessa ordem. Justificar não bloqueia; "Voltar" encerra sem avançar.
 * A justificativa vai no POST da criação, até 500 caracteres.
 */
export function useVerificacoesEquipe(params: VerificacoesEquipeParams) {
  const [etapa, setEtapa] = useState<EtapaVerificacaoEquipe | null>(null);
  const [verificando, setVerificando] = useState(false);
  const fila = useRef<EtapaVerificacaoEquipe[]>([]);
  const justificativas = useRef<{ conflito?: string; indisponibilidade?: string }>({});
  /** Conflito e indisponibilidade na mesma fila: dividem o limite da justificativa. */
  const ambasJustificativas = useRef(false);
  const acaoFinal = useRef(params.acaoFinal);
  acaoFinal.current = params.acaoFinal;

  function avancar() {
    const proxima = fila.current.shift() ?? null;
    setEtapa(proxima);
    if (!proxima) acaoFinal.current(combinarJustificativasEquipe(justificativas.current));
  }

  function iniciar(
    conflitos: ConflitoNovaAlocacao[],
    indisponiveis: IndisponibilidadeNovaAlocacao[],
    incompletos: PapelIncompletoItem[] | null,
  ) {
    justificativas.current = {};
    ambasJustificativas.current = conflitos.length > 0 && indisponiveis.length > 0;
    fila.current = [
      ...(conflitos.length > 0 ? [{ tipo: "conflitos" as const, conflitos }] : []),
      ...(indisponiveis.length > 0
        ? [{ tipo: "indisponivel" as const, itens: indisponiveis }]
        : []),
      ...(incompletos ? [{ tipo: "incompleta" as const, itens: incompletos }] : []),
    ];
  }

  async function verificar() {
    if (verificando || etapa || !params.validar()) return;

    const { obra, novas } = params;
    setVerificando(true);
    let carga: CargaTrabalhoProfissional[] = [];
    try {
      // Sem profissional na equipe não há conflito nem indisponibilidade a consultar.
      if (novas.length > 0) {
        carga = await carregarAlocacoesDoPeriodo(
          { inicio: obra.inicio, fim: obra.fim },
          novas.map((nova) => nova.usuarioId),
        );
      }
    } catch {
      params.onErroVerificacao(VERIFICACAO_ERROR_MESSAGE);
      return;
    } finally {
      setVerificando(false);
    }

    const ids = novas.map((nova) => nova.usuarioId);
    iniciar(
      detectarConflitos(novas, obra, carga),
      detectarIndisponibilidades(novas, unirIndisponibilidades(ids, carga, params.listagem)),
      papeisObrigatoriosPendentes(params.resumoObrigatorios).length > 0
        ? params.resumoObrigatorios
        : null,
    );
    avancar();
  }

  /**
   * Divergência apontada pelo servidor no POST (409): reabre os diálogos com os dados dele; ao
   * confirmar, `acaoFinal` roda de novo com a justificativa informada.
   */
  function reabrirComDivergencias(
    conflitos: ConflitoNovaAlocacao[],
    indisponiveis: IndisponibilidadeNovaAlocacao[],
  ) {
    iniciar(conflitos, indisponiveis, null);
    setEtapa(fila.current.shift() ?? null);
  }

  function confirmarConflitos(justificativa: string) {
    justificativas.current.conflito = justificativa;
    avancar();
  }

  function confirmarIndisponivel(justificativa: string) {
    justificativas.current.indisponibilidade = justificativa;
    avancar();
  }

  function cancelar() {
    fila.current = [];
    justificativas.current = {};
    setEtapa(null);
  }

  const maxJustificativa =
    etapa && etapa.tipo !== "incompleta"
      ? limiteJustificativaEquipe(
          etapa.tipo,
          ambasJustificativas.current,
          justificativas.current.conflito?.length,
        )
      : JUSTIFICATIVA_EQUIPE_MAX;

  return {
    verificar,
    verificando,
    etapa,
    maxJustificativa,
    confirmarConflitos,
    confirmarIndisponivel,
    confirmarIncompleta: avancar,
    cancelar,
    reabrirComDivergencias,
  };
}
